import React, { useEffect, useState, useRef } from "react";
import { Modal, Box } from "@mui/material";
import "./CustomFileModal.css";
import { CustomPdfModal } from "../PDF/Modal";

export interface CustomFileModalProps {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  imageName?: string;
  enablePanzoom?: boolean;
}

const CustomFileModal: React.FC<CustomFileModalProps> = ({
  open,
  onClose,
  imageSrc,
  imageName,
  enablePanzoom = false,
}) => {
  const lowerSrc = (imageSrc || "").toLowerCase();
  const lowerName = (imageName || "").toLowerCase();
  const isPdf =
    lowerSrc.startsWith("data:application/pdf") ||
    /\.pdf(\?|#|$)/.test(lowerSrc) ||
    lowerSrc.includes("application%2Fpdf") ||
    lowerSrc.includes("content-type=application/pdf") ||
    (lowerSrc.startsWith("blob:") && lowerSrc.includes("pdf")) ||
    lowerName.endsWith(".pdf");

  const [isMobile, setIsMobile] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousOpenRef = useRef(open);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    if (contentRef.current && isPdf) {
      contentRef.current.style.overflow = enablePanzoom ? "hidden" : "auto";
    }
  }, [isPdf, enablePanzoom]);

  useEffect(() => {
    if (!open && previousOpenRef.current && isLandscape) {
      requestAnimationFrame(() => {
        setIsLandscape(false);
      });
    }
    previousOpenRef.current = open;
  }, [open, isLandscape]);

  useEffect(() => {
    if (open && isPdf && isMobile && imageSrc) {
      if (imageSrc.startsWith("data:application/pdf;base64,")) {
        try {
          const base64Data = imageSrc.split(",")[1];
          const binaryData = atob(base64Data);
          const bytes = new Uint8Array(binaryData.length);
          for (let i = 0; i < binaryData.length; i++)
            bytes[i] = binaryData.charCodeAt(i);
          const blob = new Blob([bytes], { type: "application/pdf" });
          const objectUrl = URL.createObjectURL(blob);
          setTimeout(() => {
            window.open(objectUrl, "_blank", "noopener,noreferrer");
            onClose();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
          }, 100);
        } catch {
          setTimeout(() => {
            window.open(imageSrc, "_blank", "noopener,noreferrer");
            onClose();
          }, 100);
        }
      } else {
        setTimeout(() => {
          window.open(imageSrc, "_blank", "noopener,noreferrer");
          onClose();
        }, 100);
      }
    }
  }, [open, isPdf, isMobile, imageSrc, onClose]);

  if (isMobile && isPdf) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="image-modal-title"
      aria-describedby="image-modal-description"
    >
      <Box
        className={`image-modal-container ${isPdf ? "pdf-mode" : ""} ${isPdf && isLandscape ? "pdf-landscape" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <Box className="image-modal-header">
          <button
             onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="image-modal-close-button"
            aria-label="閉じる"
          />
        </Box>

        <Box
          ref={contentRef}
          className={`image-modal-content ${isPdf ? "pdf-mode" : ""} ${isPdf && isLandscape ? "pdf-landscape" : ""}`}
        >
          {isPdf ? (
            <CustomPdfModal
              pdfUrl={imageSrc}
              onOrientationChange={setIsLandscape}
              enablePanzoom={enablePanzoom}
            />
          ) : (
            <img
              src={imageSrc}
              alt={imageName || "拡大画像"}
              className="image-modal-image"
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default CustomFileModal;
